// HubHeader — the ONE page header every agent-end hub uses (U1, 2026-07-09).
//
// Replaces the per-hub hand-rolled <motion.header> pattern. Two reasons:
//   1. The framer-motion opacity entrance froze at ~0.08 opacity whenever
//      rAF was throttled (hidden tab, low-power mode) — the page title was
//      the faintest text on the page. A page header must never depend on
//      JS animation to become visible.
//   2. Every hub duplicated the same 20 lines with slightly different
//      classes — each copy a fresh chance to forget a theme variant.
//
// No entrance animation at all: the header is above the fold and its
// luxury comes from the icon chip + type hierarchy, not a 300ms fade.

import Icon from '../../components/Icon';
import { T } from '../lib/textStyles';

type Tone = 'brand' | 'emerald' | 'sky' | 'amber' | 'indigo' | 'violet';

const CHIP: Record<Tone, string> = {
  brand:   'bg-brand-50 text-brand-700 ring-brand-100 dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/30',
  emerald: 'bg-emerald-50 text-emerald-700 ring-emerald-100 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
  sky:     'bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30',
  amber:   'bg-amber-50 text-amber-700 ring-amber-100 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30',
  indigo:  'bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-400/15 dark:text-indigo-300 dark:ring-indigo-400/30',
  violet:  'bg-violet-50 text-violet-700 ring-violet-100 dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30',
};

export default function HubHeader({
  icon, title, subtitle, tone = 'brand', right,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  tone?: Tone;
  right?: React.ReactNode;
}) {
  return (
    <header className="px-2 lg:px-4">
      <div className="flex items-center gap-3">
        <span className={`grid place-items-center w-10 h-10 rounded-xl ring-1 shrink-0 ${CHIP[tone]}`}>
          <Icon name={icon} size={22} filled />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className={`text-xl sm:text-2xl font-display font-semibold tracking-tight ${T.primary}`}>
            {title}
          </h1>
          {subtitle && (
            <p className={`text-[12.5px] truncate ${T.tertiary}`}>{subtitle}</p>
          )}
        </div>
        {right}
      </div>
    </header>
  );
}
